import React, { useState } from "react";
import { CalendarCheck2, CheckSquare, Square } from "lucide-react";
import DateInput from "@components/forms/DateInput";
import Form from "react-bootstrap/Form";
import { Row, Col } from "react-bootstrap";
import { Button } from "rsuite";
import IController from "@components/forms/IController";
import { Table } from "react-bootstrap";
import ManageHeader from "@components/ManageHeader";
import { useTranslation } from "react-i18next";
import { Config } from "@config/config";
import { QueryClient, QueryClientProvider, useQuery } from "react-query";
import AJXLoader from "@components/AJXLoader";
import LoadError from "@components/LoadError";
import { apiSend, getRoute } from "@services/axios";
import { useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import SController from "@components/forms/SController";
import { saveChangeRoomStatusAndAvailability } from "./Actions";
import { dateFrEn } from "@global/actions";

const queryClient = new QueryClient();

const PricingOptionsAndAvailability = (props) => {
  const { t } = useTranslation();
  const config = new Config(t);
  return (
    <section>
      <ManageHeader
        title={t("")}
        content={t("ho_act_tarif_rest_txt")}
        icon={<CalendarCheck2 size={25} strokeWidth={1.5} />}
      />

      <QueryClientProvider client={queryClient}>
        <DataRoom t={t} {...props} config={config} />
      </QueryClientProvider>
    </section>
  );
};

const DataRoom = (props) => {
  const t = props.t;
  const [loading, setLoading] = useState(false);
  const id = useParams().id;
  const [rooms, setRooms] = useState([]);
  const [selectedRooms, setSelectedRooms] = useState([]);
  const [showBulkUpdate, setShowBulkUpdate] = useState(false);
  const [bulkValues, setBulkValues] = useState({
    rooms_available: "",
    min_price: "",
    is_stop_sales: ""
  });
  
  const [dateFrom, setDateFrom] = useState(
    new Date().toLocaleDateString("fr-FR")
  );
  const [dateTo, setDateTo] = useState(new Date().toLocaleDateString("fr-FR"));

  const {
    control,
    register,
    formState: { errors },
    handleSubmit,
    setValue
  } = useForm({});

  const load = async () => {
    const response = await apiSend(
      [getRoute("PRODUCT_RETRIEVE"), id, "rooms"].join("/"),
      "get"
    );
    if (response?.data?.request?.status === true) {
      setRooms(response?.data?.result?.rooms);
    }

    return response?.data?.request?.status;
  };

  const { isLoading, error } = useQuery("repoData", () => load());
  
  if (isLoading) return <AJXLoader />;
  if (error) return <LoadError message={t("not_found")} />;

  const actions = [
    { title: t("ho_act_segmentActiona1"), value: "" },
    { title: t("closed"), value: 1 },
    { title: t("opened"), value: 0 },
  ];

  // Handle select all rooms
  const handleSelectAll = () => {
    if (selectedRooms.length === rooms.length) {
      setSelectedRooms([]);
      setShowBulkUpdate(false);
    } else {
      setSelectedRooms(rooms.map(room => room.id));
      setShowBulkUpdate(true);
    }
  };

  // Handle individual room selection
  const handleSelectRoom = (roomId) => {
    let newSelected = [...selectedRooms];
    if (newSelected.includes(roomId)) {
      newSelected = newSelected.filter(id => id !== roomId);
    } else {
      newSelected.push(roomId);
    }
    setSelectedRooms(newSelected);
    setShowBulkUpdate(newSelected.length > 0);
  };

  // Apply bulk updates to selected rooms
  const applyBulkUpdate = () => {
    selectedRooms.forEach(roomId => {
      if (bulkValues.rooms_available !== "") {
        setValue(`${roomId}.rooms_available`, bulkValues.rooms_available);
      }
      if (bulkValues.min_price !== "") {
        setValue(`${roomId}.min_price`, bulkValues.min_price);
      }
      if (bulkValues.is_stop_sales !== "") {
        setValue(`${roomId}.is_stop_sales`, bulkValues.is_stop_sales);
      }
    });
    
    // Reset bulk form after applying
    setBulkValues({
      rooms_available: "",
      min_price: "",
      is_stop_sales: ""
    });
  };

  const onChangeDate = (from, to) => {
    setDateFrom(from);
    setDateTo(to);
  };

  const onSubmit = async (data) => {
    setLoading(true);
    const payload = {
      dateFrom: dateFrEn(dateFrom),
      dateTo: dateFrEn(dateTo),
      rooms: data,
    };
    console.log('payload',payload)
    await saveChangeRoomStatusAndAvailability(id, payload);
    setLoading(false);
  };

  const days = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

  var rule = {
    type: "number",
    placeholder: t("rooms_available"),
    messageError: t("is__required"),
    className: "input-blue text-dark w-100 p-3 border fw-light ",
    size: "md",
  };

  var amount = {
    type: "number",
    placeholder: t("amount"),
    messageError: t("is__required"),
    className: "input-blue text-dark w-100 p-3 border fw-light ",
    size: "md",
  };

  return (
    <>
      <div className="container my-4">
        <Row className="m-auto mx-1 p-5 d-flex flex-column bg-white rounded">
          <Col className="">
            <span className="fs-4 fw-bold text-dark">{t("ho_act_select")}</span>
            <p className="fs-8 my-2">
              {t("ho_act_segment_selectp")}
              <br />
              <b>{t("exemple")}: </b>
              {t("ho_act_segmentExp")}
            </p>
          </Col>
          <Row className="d-flex flex-row align-items-center">
            <Col sm="12" md="6" className="d-flex align-items-start mt-3 py-2">
              <DateInput
                rightLabel={t("departure_date")}
                leftLabel="End date"
                onDateChange={onChangeDate}
                dateFrom={dateFrom}
                dateTo={dateTo}
              />
            </Col>
            <Col sm="12" md="6" className="d-flex flex-row flex-wrap mt-3 py-2">
              {days.map((day) => (
                <span key={day} className="d-flex flex-row me-3 mb-2">
                  <Form.Check
                    type="checkbox"
                    defaultChecked
                    className="me-1"
                  />
                  <span className="mt-1">{t(day)}</span>
                </span>
              ))}
            </Col>
          </Row>
        </Row>
      </div>

      <div className="container my-4 d-flex flex-column">
        <span className="fs-4 fw-bold text-dark">{t("ho_act_segmentMAJ")}</span>
        <p xl="8" className="fs-8 mt-2 mb-4">
          {t("ho_act_segmentMAJp")}
        </p>

        {/* Selection Controls */}
        <div className="mb-3 d-flex align-items-center">
          <Button
            appearance="subtle"
            onClick={handleSelectAll}
            className="d-flex align-items-center me-3"
          >
            {selectedRooms.length === rooms.length ? (
              <CheckSquare size={20} className="me-2" />
            ) : (
              <Square size={20} className="me-2" />
            )}
            {t("select_all")} ({selectedRooms.length}/{rooms.length})
          </Button>
          
        
        </div>

        {/* Bulk Update Panel */}
        {showBulkUpdate && (
          <div className="bg-light p-4 mb-4 rounded border">
            
            <Row className="align-items-end">
              <Col sm="12" md="6" lg="3">
                <label className="form-label">{t("rooms_available")}</label>
                <input
                  type="number"
                  className="form-control"
                  placeholder={t("rooms_available")}
                  value={bulkValues.rooms_available}
                  onChange={(e) => setBulkValues({
                    ...bulkValues,
                    rooms_available: e.target.value
                  })}
                />
              </Col>
              <Col sm="12" md="6" lg="3">
                <label className="form-label">{t("amount")}</label>
                <input
                  type="number"
                  className="form-control"
                  placeholder={t("amount")}
                  value={bulkValues.min_price}
                  onChange={(e) => setBulkValues({
                    ...bulkValues,
                    min_price: e.target.value
                  })}
                />
              </Col>
              <Col sm="12" md="6" lg="3">
                <label className="form-label">{t("status")}</label>
                <select
                  className="form-select"
                  value={bulkValues.is_stop_sales}
                  onChange={(e) => setBulkValues({
                    ...bulkValues,
                    is_stop_sales: e.target.value
                  })}
                >
                  <option value="">{t("no_changes")}</option>
                  <option value="0">{t("opened")}</option>
                  <option value="1">{t("closed")}</option>
                </select>
              </Col>
              <Col sm="12" md="6" lg="3">
                <Button
                  
                 
                  onClick={applyBulkUpdate}
                 
                  className="bg-blue text-white"
                >
                  {t("apply_to_selected")}
                </Button>
              </Col>
            </Row>
          </div>
        )}

        <Row
          className="m-auto mt-3 mx-1 px-3 py-5 d-flex flex-row bg-white rounded border"
          style={{ boxShadow: "0px 0px 10px 0px rgba(0,0,0,0.1)" }}
        >
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="">
           
              <Table responsive className="rounded custom-hover" hover>
              <thead>
                <tr className="">
                  <th className="fs-6 p-3" >
                    <Form.Check
                      type="checkbox"
                      checked={selectedRooms.length === rooms.length}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th className="fs-6 p-3">{t("ho_act_segmentType")}</th>
                  <th className="fs-6 p-3">{t("ho_act_segmentDispo")}</th>
                  <th className="fs-6 p-3">{t("amount")}</th>
                  <th className="fs-6 p-3">{t("actions")}</th>
                 
                </tr>
              </thead>
              <tbody>
                {rooms.map((room, index) => (
                  <tr 
                    key={index}
                    className={selectedRooms.includes(room.id) ? "table-primary" : ""}
                  >
                    <td className="p-4">
                      <Form.Check
                        type="checkbox"
                        checked={selectedRooms.includes(room.id)}
                        onChange={() => handleSelectRoom(room.id)}
                      />
                    </td>
                    <td className="p-3 text-center align-middle">
                      {room.type} {room.option}
                    </td>
                    <td className="p-3 text-center align-middle">
                      <IController
                        elment={room.id + `.rooms_available`}
                        item={rule}
                        control={control}
                        errors={errors}
                        register={register}
                      />
                    </td>
                    <td className="p-3 text-center align-middle">
                      <IController
                        elment={room.id + `.min_price`}
                        item={amount}
                        control={control}
                        errors={errors}
                        register={register}
                      />
                    </td>
                    <td className="p-3 text-center align-middle">
                      <SController
                        data={actions}
                        title="status"
                        elment={room.id + `.is_stop_sales`}
                        register={register}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            </div>
            

            <div className="d-flex justify-content-between align-items-center mt-4">
              <Button
                className="px-5"
                appearance="primary"
                color="cyan"
                type="submit"
              >
                {loading ? (
                  <span className="spinner-border spinner-border-sm m-1"></span>
                ) : (
                  t("confirm")
                )}
              </Button>
        
            </div>
          </form>
        </Row>
      </div>
    </>
  );
};

export default PricingOptionsAndAvailability;